<template>
    <div>
        <v-dialog v-model="internalValue">
            <v-card class="stock-media-card">
                <div class="d-flex flex-column flex-grow-1">
                    <div class="p-3 controls-header">
                        <v-row>
                            <v-col>
                                <div>
                                    <v-btn-toggle
                                        v-if="mode !== 'themeBuilder'"
                                        dense
                                        v-model="mediaType"
                                        mandatory
                                        class="section-toggle"
                                    >
                                        <v-btn value="photo" class="no-text-transform"> Photos </v-btn>

                                        <v-btn value="video" class="no-text-transform"> Videos </v-btn>
                                    </v-btn-toggle>
                                </div>
                            </v-col>

                            <v-col>
                                <div>
                                    <h2 v-if="mode === 'themeBuilder'" class="text-center mb-4">Theme Builder</h2>
                                    <TextButtonInput
                                        class="search-input"
                                        v-model.lazy="searchText"
                                        :readonly="false"
                                        clearable
                                        @click:clear="
                                            searchText = '';
                                            searchPexels();
                                        "
                                        @submit="searchPexels()"
                                    >
                                        <template #action>
                                            <v-btn class="no-text-transform" depressed @click="searchPexels()">
                                                Search</v-btn
                                            >
                                        </template>
                                    </TextButtonInput>
                                    <v-btn
                                        v-if="!state.isFamily && !state.isContributor"
                                        class="no-text-transform"
                                        @click="customUpload"
                                        depressed
                                        block
                                        dark
                                    >
                                        <font-awesome-icon icon="fas fa-upload" class="mr-2"/>
                                        Upload your own media
                                    </v-btn>
                                </div>
                            </v-col>

                            <v-col>
                                <div class="d-flex justify-end">
                                    <v-btn
                                        class="no-text-transform close-button"
                                        @click="internalValue = false"
                                        depressed
                                        dark
                                        >Close</v-btn
                                    >
                                </div>
                            </v-col>
                        </v-row>
                    </div>

                    <div
                        v-show="mediaType == 'photo' && photos.results.length"
                        ref="scrollContainer"
                        class="main-section"
                    >
                        <div class="media-grid">
                            <div
                                class="media-result"
                                v-for="result in photos.results"
                                :key="`photo_${result.id}`"
                                :style="{ aspectRatio: result.width / result.height }"
                                v-click-single-double="{
                                    click: () => initPreview(result),
                                    dblclick: () => initImport(result.src.original),
                                }"
                            >
                                <img
                                    class="media-img"
                                    v-if="result.src.medium"
                                    :src="result.src.medium"
                                    loading="lazy"
                                />
                            </div>
                        </div>

                        <div v-if="photos.nextPage" v-intersect="onIntersect" class="d-flex justify-center p-4">
                            <v-btn
                                @click="fetchPexelsNextPage"
                                class="no-text-transform"
                                depressed
                                :loading="loading"
                                :disabled="loading || !photos.nextPage"
                                >Load More</v-btn
                            >
                        </div>
                    </div>

                    <div
                        v-show="mediaType == 'video' && videos.results.length"
                        ref="scrollContainer"
                        class="main-section"
                    >
                        <div class="media-grid">
                            <div
                                class="media-result"
                                v-for="(result, index) in videos.results"
                                :key="`video-${result.id}`"
                                :style="{ aspectRatio: getAspectRatio(result.width, result.height) }"
                                v-click-single-double="{
                                    click: () => initPreview(result),
                                    dblclick: () => initVideoImport(result),
                                }"
                            >
                                <img :src="result.image" class="media-video" loading="lazy" />

                                <div class="duration-tag">{{ result.duration | formatTimeStamp }}</div>
                            </div>
                        </div>
                        <div v-if="videos.nextPage" v-intersect="onIntersect" class="d-flex justify-center p-4">
                            <v-btn
                                class="no-text-transform"
                                depressed
                                :loading="loading"
                                :disabled="loading || !videos.nextPage"
                                >Load More</v-btn
                            >
                        </div>
                    </div>
                    <div
                        v-if="
                            (!loading && mediaType === 'video' && !videos.results.length) ||
                            (mediaType === 'photo' && !photos.results.length)
                        "
                        class="no-results-container"
                    >
                        <h6 class="header my-5">
                            No results
                            {{
                                currentSearchString && currentSearchString.length
                                    ? `found for "${currentSearchString}".`
                                    : 'to display.'
                            }}
                        </h6>

                        <div class="suggestions">
                            <p class="m-0">Try these popular searches:</p>
                            <a
                                @click.stop.prevent="
                                    searchText = suggestion;
                                    searchPexels();
                                "
                                v-for="suggestion in searchSuggestions"
                                :key="suggestion"
                            >
                                <span class="tag">{{ suggestion }}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="downloadingImport" max-width="400px">
            <v-card>
                <v-card-title> Importing.. </v-card-title>
                <v-card-text>
                    <v-progress-linear
                        rounded
                        v-model="downloadImportProgress"
                        :color="downloadImportProgress === 100 ? 'success' : 'primary'"
                        dark
                        height="20"
                        >{{ downloadImportProgress }}%</v-progress-linear
                    >
                </v-card-text>
                <v-card-actions class="d-flex justify-center">
                    <v-btn @click="cancelDownload" depressed color="error" class="no-text-transform">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog @click:outside="resetPreview" v-model="previewModal" max-width="80vw">
            <v-card class="preview-modal" style="position: relative" v-if="selectedMedia">
                <v-card-title class="preview-modal-title px-4">
                    <p v-if="!selectedMedia.alt">{{ selectedMedia.url | urlToTitle }}</p>
                    <p v-else>{{ selectedMedia.alt }}</p>
                    <v-btn @click="resetPreview" plain small class="m-0 p-0">
                        <font-awesome-icon icon="fa-solid fa-x"></font-awesome-icon>
                    </v-btn>
                </v-card-title>
                <div class="preview-modal-body">
                    <v-card-text class="media-user" v-if="selectedMedia.user">
                        <div class="username">
                            {{ selectedMedia.user.name }}
                        </div>
                        <div class="" v-if="selectedMedia.user.url">
                            <a :href="selectedMedia.user.url" target="_blank" class="profile-link"
                                >View profile <font-awesome-icon icon="fa-regular fa-external-link"></font-awesome-icon
                            ></a>
                        </div>
                    </v-card-text>
                    <v-card-text class="media-user" v-else>
                        <div class="username">
                            {{ selectedMedia.photographer }}
                        </div>
                        <div class="" v-if="selectedMedia.photographer_url">
                            <a :href="selectedMedia.photographer_url" target="_blank" class="profile-link"
                                >View profile <font-awesome-icon icon="fa-regular fa-external-link"></font-awesome-icon
                            ></a>
                        </div>
                    </v-card-text>
                    <v-card-text class="media-container p-0">
                        <video
                            v-if="selectedPreviewUrl"
                            ref="previewPlayer"
                            style="width: 100%"
                            :src="selectedPreviewUrl"
                            class="media-video"
                            playsinline
                            :muted="mode === 'themeBuilder'"
                            loop
                            controls
                        ></video>
                        <img :src="selectedMedia.src.large" v-else />
                    </v-card-text>
                    <v-card-text class="media-details">
                        <div class="info-panel">
                            <div class="info-panel-header">Details</div>
                            <div class="info-section">
                                <div class="info-section-header">Type:</div>
                                <div class="info-section-detail">
                                    {{ selectedMedia.duration ? 'Video' : 'Photo' }}
                                </div>
                            </div>
                            <div class="info-section" v-if="selectedMedia.duration">
                                <div class="info-section-header">Duration:</div>
                                <div class="info-section-detail">{{ selectedMedia.duration }}s</div>
                            </div>
                            <div class="info-section" v-if="selectedMedia.avg_color">
                                <div class="info-section-header">Average Color:</div>
                                <div class="info-section-detail">
                                    <div class="swatch" :style="{ backgroundColor: selectedMedia.avg_color }"></div>
                                    <span class="tag swatch-code">{{ selectedMedia.avg_color }}</span>
                                </div>
                            </div>
                            <div class="info-section">
                                <div class="info-section-header">Dimensions:</div>
                                <div class="info-section-detail">
                                    {{ selectedMedia.width }} x {{ selectedMedia.height }}
                                </div>
                            </div>
                        </div>
                        <div class="info-panel" v-if="selectedMedia.tags && selectedMedia.tags.length">
                            <div class="info-panel-header">Tags</div>
                            <div class="tags">
                                <span class="tag light" v-for="tag in selectedMedia.tags" :key="tag">
                                    {{ tag }}
                                </span>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-actions center class="media-actions">
                        <v-spacer></v-spacer>
                        <template v-if="selectedMedia.duration">
                            <!-- Actions for Video -->
                            <v-btn
                                v-if="mode === 'themeBuilder'"
                                class="no-text-transform"
                                @click="initVideoUrlImport(selectedMedia)"
                                color="success"
                            >
                                Import
                            </v-btn>
                            <template v-else>
                                <v-btn
                                    class="no-text-transform"
                                    color="primary"
                                    @click="editVideo(selectedMedia)"
                                >
                                    Edit
                                </v-btn>
                                <v-btn
                                    class="no-text-transform"
                                    @click="initVideoImport(selectedMedia)"
                                    color="success"
                                >
                                    Import
                                </v-btn>
                            </template>
                        </template>
                        <template v-else>
                            <!-- Actions for photos -->
                            <v-btn
                                class="no-text-transform"
                                color="primary"
                                @click="editMedia(selectedMedia.src.large)"
                            >
                                Edit
                            </v-btn>
                            <v-btn
                                class="no-text-transform"
                                @click="initImport(selectedMedia.src.original)"
                                color="success"
                            >
                                Import
                            </v-btn>
                        </template>
                    </v-card-actions>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { debounce, capitalize, startCase } from 'lodash';
import { mapActions } from 'pinia';
import moment from 'moment';
import SlideUpload from '../SlideUpload.vue';
import TextButtonInput from '@/components/ui/TextButtonInput.vue';
import { useTributeVideoStore } from '@/stores/tributeVideo';
import { useGlobalState } from '@/stores/globalState';

export default {
    setup() {
        return {
            tributeVideoStore: useTributeVideoStore(),
        }
    },
    data() {
        return {
            visible: this.value,
            searchText: null,
            // used for displaying in the no results modal
            // so that it doesn't change as the user retypes their query
            currentSearchString: null,
            page: 2,
            perPage: 20,
            loading: true,
            photos: {
                results: [],
                next_page: null,
            },
            videos: {
                results: [],
                next_page: null,
            },
            mediaType: 'photo',
            downloadingImport: false,
            downloadImportProgress: 0,
            cancelTokenSource: null,
            selectedPreviewUrl: null,
            selectedMedia: null,
            previewModal: false,
            fallbackSearchText: 'landscape',
            searchSuggestions: ['Mountains', 'Seashore', 'Harvest', 'Birds', 'Clouds'],
        };
    },
    inject: ['state'],
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            required: false,
        },
    },
    components: { SlideUpload, TextButtonInput },
    computed: {
        internalValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        eventId: {
            get() {
                return this.tributeVideoStore.eventId;
            },
        },
    },
    watch: {
        //Disable debounced search for now,
        //200 req per hour
        //20k per month
        // searchText(newVal) {
        //     console.log(newVal, 'searchtext updated')
        //     this.debouncedSearch(newVal);
        // }
        mediaType(newVal) {
            this.searchPexels();
        },
        internalValue(newVal) {
            if (!newVal) return;

            this.searchPexels();
        },
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');

            return `${formattedMinutes}:${formattedSeconds}`;
        },
        urlToTitle(url) {
            const titleString = url.split('/')[4].replace(/\d+/, '');
            if (titleString) {
                return startCase(titleString);
            }
            return 'Preview';
        },
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar']),
        initPreview(media) {
            let isVideo = media.duration && !media.alt;
            // Pexels video &
            if (isVideo) {
                this.selectedPreviewUrl = this.getPreferredMediaLink(media);

                if (!this.selectedPreviewUrl) {
                    this.showSnackbar({ message: 'Unable to find preview source', color: 'error' });
                    return;
                }
            }
            this.previewModal = true;
            this.selectedMedia = media;
            // console.log('previewing media', media);
            this.$nextTick(() => {
                if (isVideo) {
                    const previewPlayer = this.$refs.previewPlayer;

                    if (previewPlayer) {
                        previewPlayer.play();
                    }
                }
            });
        },
        resetPreview() {
            this.previewModal = false;
            this.selectedPreviewUrl = null;
            this.selectedMedia = null;
        },
        onIntersect(entries) {
            if (entries[0].isIntersecting) {
                this.fetchPexelsNextPage();
            }
        },
        scrollToTop() {
            const el = this.$refs.scrollContainer;
            if (el) el.scrollTop = 0;
        },
        // async getPexelsCuratedPhotos() {

        //     try {
        //         this.loading = true;
        //         this.scrollToTop();

        //         const resp = await this.axios({
        //             method: 'get',
        //             url: process.env.VUE_APP_PEXELS_API_BASE + 'curated',
        //             params: {
        //                 per_page: this.perPage, // optional: control number of results
        //                 page: this.page,
        //             },
        //             headers: {
        //                 Authorization: process.env.VUE_APP_PEXELS_API_KEY,
        //             },
        //         });

        //         if (resp.data.photos) {
        //             this.photos.results = resp.data.photos;
        //             this.photos.nextPage = resp.data.next_page;
        //         }
        //     } catch (error) {
        //         console.log(error, 'error fetching from pexels');
        //     } finally {
        //         this.loading = false;
        //     }
        // },
        async searchPexels() {
            const text = this.searchText || this.fallbackSearchText;
            this.currentSearchString = this.searchText?.length ? `${this.searchText}` : null;
            const mediaType = this.mediaType;
            const isVideo = mediaType === 'video';

            try {
                this.loading = true;
                this.scrollToTop();

                const endpointMod = isVideo ? 'videos/' : '';

                const resp = await this.axios({
                    method: 'get',
                    url: process.env.VUE_APP_PEXELS_API_BASE + endpointMod + 'search',
                    params: {
                        query: text,
                        per_page: this.perPage,
                        page: this.page,
                        orientation: 'landscape',
                    },
                    headers: {
                        Authorization: process.env.VUE_APP_PEXELS_API_KEY,
                    },
                });

                if (isVideo) {
                    if (resp.data.videos) {
                        let filteredResults = this.filterFilesBySize(resp.data.videos);

                        if (this.mode === 'themeBuilder') {
                            filteredResults = this.filterFilesByMinDurationSeconds(filteredResults, 10);
                        }

                        this.videos.results = filteredResults;
                        this.videos.nextPage = resp.data.next_page;
                    }
                } else {
                    if (resp.data.photos) {
                        this.photos.results = resp.data.photos;
                        this.photos.nextPage = resp.data.next_page;
                    }
                }
            } catch (error) {
                console.log(error, 'error fetching from pexels');
            } finally {
                this.loading = false;
            }
        },
        filterFilesBySize(videoResults) {
            const maxWidth = 1920;

            return videoResults.reduce((filtered, video) => {
                const files = video.video_files.filter(file => file.width <= maxWidth);
                if (files.length > 0) {
                    // Find the file with the largest width (and corresponding height)
                    const largestFile = files.reduce((largest, file) => (file.width > largest.width ? file : largest));

                    filtered.push({
                        ...video,
                        video_files: files,
                        width: largestFile.width,
                        height: largestFile.height,
                    });
                }
                return filtered;
            }, []);
        },
        filterFilesByMinDurationSeconds(videoResults, minDurationSeconds) {
            return videoResults.filter(res => res.duration >= minDurationSeconds);
        },
        cancelDownload() {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel('User canceled download');
            }
            this.downloadingImport = false;
        },
        async fetchPexelsNextPage() {
            const isVideo = this.mediaType === 'video';

            if (isVideo && !this.videos.nextPage) {
                return;
            }

            if (!isVideo && !this.photos.nextPage) {
                return;
            }

            const endpoint = isVideo ? this.videos.nextPage : this.photos.nextPage;

            try {
                this.loading = true;

                const resp = await this.axios({
                    method: 'get',
                    url: endpoint,
                    headers: {
                        Authorization: process.env.VUE_APP_PEXELS_API_KEY,
                    },
                });

                if (isVideo) {
                    if (resp.data.videos) {
                        let filteredResults = this.filterFilesBySize(resp.data.videos);

                        if (this.mode === 'themeBuilder') {
                            filteredResults = this.filterFilesByMinDurationSeconds(filteredResults, 10);
                        }

                        this.videos.nextPage = resp.data.next_page;

                        this.videos.results = this.videos.results.concat(filteredResults);
                        this.videos.nextPage = resp.data.next_page;
                    }
                } else {
                    if (resp.data.photos) {
                        this.photos.results = this.photos.results.concat(resp.data.photos);
                        this.photos.nextPage = resp.data.next_page;
                    }
                }
            } catch (error) {
                console.log(error, 'error fetching nex page');
            } finally {
                this.loading = false;
            }
        },
        getPreferredMediaLink(video) {
            const filesGrouped = _.groupBy(_.orderBy(video?.video_files, 'width', 'desc'), 'quality');

            const file =
                filesGrouped?.hd?.[0] || filesGrouped?.sd?.[0] || video.video_files.find(file => file.height === 720);

            if (!file?.link) {
                throw new Error('No source found for import link');
            }

            return file.link;
        },
        initVideoImport(video) {
            const link = this.getPreferredMediaLink(video);

            this.initImport(link);
        },
        initVideoUrlImport(video) {
            const link = this.getPreferredMediaLink(video);

            this.$emit('import-to-editor', { link, video });
            this.$nextTick(() => {
                this.resetPreview();
                this.internalValue = false;
            });
        },
        async initImport(url) {
            if (this.mode === 'themeBuilder') return;
            const file = await this.fetchAsFile(url);

            if (this.eventId) {
                this.$emit('init-import', file);
            }
            this.$nextTick(() => {
                this.resetPreview();
                this.internalValue = false;
            });
        },
        async editMedia(url) {
            if (this.eventId) {
                this.$emit('edit-media', url, 0);
                this.internalValue = false;
            }
            this.resetPreview();
        },
        async editVideo(video) {
            if (this.eventId) {
                const filesGrouped = _.groupBy(_.orderBy(video?.video_files, 'width', 'desc'), 'quality');
                const file =
                    filesGrouped?.hd?.[0] || filesGrouped?.sd?.[0] || video.video_files.find(file => file.height === 720);
                this.$emit('edit-media', file.link, 1);
                this.internalValue = false;
            }
            this.resetPreview();
        },
        async fetchAsFile(url) {
            try {
                this.downloadingImport = true;
                this.cancelTokenSource = this.axios.CancelToken.source();

                const blob = await this.fetchUrlData(
                    url,
                    progress => {
                        this.downloadImportProgress = Math.floor(progress);
                    },
                    this.cancelTokenSource.token,
                );

                const contentType = this.mediaType === 'photo' ? 'image/jpeg' : 'video/mp4';

                const filename = this.parseFilenameFromUrl(url);

                return new File([blob], filename, { type: contentType });
            } catch (error) {
            } finally {
                setTimeout(() => {
                    this.downloadingImport = false;
                    this.downloadImportProgress = 0;
                }, 500);
            }
        },

        async fetchUrlData(url, onProgress, cancelToken) {
            const response = await this.axios({
                method: 'get',
                url,
                responseType: 'blob',
                cancelToken,
                onDownloadProgress: progressEvent => {
                    if (progressEvent.lengthComputable && typeof onProgress === 'function') {
                        const percent = (progressEvent.loaded / progressEvent.total) * 100;
                        onProgress(percent.toFixed(1));
                    }
                },
            });

            return response.data;
        },
        parseFilenameFromUrl(url) {
            try {
                const cleaned = this.cleanTrailingSlash(url);
                return cleaned.split('/').pop()?.split('?')[0] || 'image.jpg';
            } catch (error) {
                console.error('Unable to parse filename', error);
                return 'image.jpg';
            }
        },
        cleanTrailingSlash(url) {
            let cleaned = url.replace(/\/$/, '');
            return cleaned;
        },
        getAspectRatio(width, height) {
            return (width / height).toFixed(1);
        },
        customUpload() {
            // open file picker or maybe uppy?
            // Upload to tribute bucket
            this.$emit('import-custom-media-to-editor');
        }
    },
    created() {
        this.debouncedSearch = debounce(this.searchPexels, 500);
    },
    mounted() {
        if (this.mode === 'themeBuilder') {
            this.mediaType = 'video';
        }
    },
};
</script>
<style lang="scss" scoped>
video {
    display: block;
    max-height: 800px;
}

.controls-header {
    display: flex;
    justify-content: space-between;

    @include mobile() {
        display: grid;
        grid-template-areas:
            'section-toggle close'
            'search search';
        gap: 10px;

        .section-toggle {
            grid-area: 'section-toggle';
        }
        .search-input {
            grid-area: search;
            align-self: flex-end;
        }
        .close-button {
            grid-area: close;
        }
    }
}
.media-grid {
    gap: 1rem;
    container: media-grid / inline-size;
    display: flex;
    align-items: left;
    justify-content: left;
    flex-direction: row;
    flex-wrap: wrap;
    flex-flow: row wrap;
    align-content: flex-end;

}
.media-result {
    position: relative !important;
    overflow: hidden;
    cursor: pointer;
    transition: height 0.66s ease-out;
    background: darken($light-gray, 6%);
    border-radius: 8px;
    width: calc(33% - 1rem);

    @include mobile() {
        columns: 1 100cqw;
        width: calc(100% - 1rem);
    }
    @include desktop() {
        width: calc(25% - 1rem);
    }
    @include wide() {
        width: calc(20% - 1rem);
    }
    img,
    video {
        height: 100%;
        display: block;
    }

    .duration-tag {
        border: 1px solid $medium-gray;
        position: absolute;
        bottom: 5px;
        right: 5px;
        background-color: white;
        border-radius: 4px;
        padding: 0 8px;
        font-size: 12px;
    }
}

.stock-media-card {
    height: 90vh;
    display: flex;
}

.main-section {
    flex-grow: 1;
    overflow: auto;
    padding: 14px;
}

.overlay {
    opacity: 0;
    transition: 0.2s;
    border-radius: 8px;
}

.media-result:hover .overlay {
    opacity: 1;
}

.tag {
    background: darken($light-gray, 6%);
    padding: 4px 6px;
    border-radius: 5px;
    font-weight: bold;
}
.preview-modal {
    container: preview-modal / inline-size;
    max-height: 90vh;
    max-height: 90dvh;

    .preview-modal-body {
        display: grid;
        grid-template-areas:
            'user user user'
            'media media media'
            'details details details'
            'actions actions actions';
    }
    .preview-modal-title {
        background: $light-gray;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;

        p {
            flex-grow: 1;
            margin: 0;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            display: inline-block;
        }
    }
    .media-container {
        grid-area: media;
        background: black;
        display: inline-flex;
        justify-content: center;

        img,
        video {
            object-fit: contain;
            width: 100%;
            max-height: 30vh;
        }
    }
    .media-user {
        grid-area: user;
        .username {
            font-weight: bold;
            font-size: 1rem;
        }
        .profile-link {
            color: lighten($medium-blue, 10%) !important;
            font-weight: bold;
        }
    }
    .media-details {
        grid-area: details;
    }
    .media-actions {
        grid-area: actions;
    }
    .info-panel {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 10px 0;
        &:after {
            width: 100% !important;
        }
        .info-panel-header {
            font-weight: bold;
            font-size: 1.1rem;
        }
        .info-section {
            display: flex;
            flex: 1;
            justify-content: space-between;
            align-items: center;
            .info-section-header {
                margin-right: 10px;
            }
            .info-section-detail {
                display: inline-flex;
                align-items: center;
                font-weight: bold;
            }
        }
        .tags {
            display: flex;
            gap: 6px;
        }
    }
    .swatch {
        height: 20px;
        width: 20px;
        aspect-ratio: 1;
        display: inline-block;
        margin-right: 6px;
    }
    .swatch-code {
        font-family: monospace;
    }
    @container (min-width: 850px) {
        .media-container {
            min-width: 65cqw;

            img,
            video {
                max-height: 60vh !important;
            }
        }
        .preview-modal-body {
            grid-template-areas:
                'media media user'
                'media media details'
                'media media actions' !important;
        }
        .media-user {
            height: 9cqh;
        }
        .media-details {
            height: 38cqh;
            gap: 10px;
            display: inline-flex;
            flex-direction: column;
        }
        .media-actions {
            display: inline-flex;
            .spacer {
                display: none;
            }
            .v-btn {
                flex: 1;
            }
        }

        .media-user,
        .info-panel:not(:last-of-type) {
            position: relative;
            &:after {
                content: '';
                border: 1px solid black;
                position: absolute;
                width: 90%;
                bottom: 0;
                left: 0;
                right: 0;
                margin: 0 auto;
            }
        }
        .info-panel {
            padding: 5px 0 26px !important;
        }
    }
}
.no-results-container {
    background: $light-gray;
    display: flex;
    flex-direction: column;
    margin: 1rem;
    padding: 1rem;
    align-items: center;
    justify-content: center;
    .header {
        font-weight: bold;
        margin: 10px 0;
    }
    .suggestions {
        gap: 10px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        .tag {
            cursor: pointer;
            transition: all 300ms ease-in-out;
            background-color: lighten($sharp-blue, 16%);
            color: white;
            &:hover {
                background-color: $sharp-blue;
                // color: $sharp-blue;
            }
        }
    }
}
</style>
